import { ActionIcon, Box, Card, Collapse, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconChevronDown, IconChevronRight, IconDeviceFloppy } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { TristateBoolean } from '../../../../../react-commons/enums/tristateBoolean';
import { CategoryDefinition, SettingClass, UpdatedSetting } from '../../../resource/resource-base/settings/types';
import { PropertyValidationInformation } from '../../util/PropertyValidationInformation';
import { BasePropertySettingComponent } from './BasePropertySettingComponent';

export const BasePropertySettingsCategoryComponent= ({ categoryDefinition, onCategorySettingsChange, onSettingChange, propertyValidationInformation }: {
    categoryDefinition: CategoryDefinition;
    onCategorySettingsChange: (category: string, settings: UpdatedSetting[]) => void;
    onSettingChange: (setting: UpdatedSetting) => void;
    propertyValidationInformation: PropertyValidationInformation;
}) => {
    const [opened, { toggle }] = useDisclosure(true);
    const [updatedSettings, setUpdatedSettings] = useState<UpdatedSetting[]>([]);

    useEffect(() => {
        setUpdatedSettings([]);
    }, [categoryDefinition.settings]);

    const label = categoryDefinition.identifier
        .split('_')
        .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
        .join(' ');

    const getSaveButton = () => {
        if (updatedSettings.length > 0) {
            return (
                <ActionIcon
                    style={{ paddingTop: '0px' }}
                    size={'sm'}
                    variant={'transparent'}
                    onClick={() => onCategorySettingsChange(categoryDefinition.identifier, updatedSettings)}
                >
                    <IconDeviceFloppy color={'var(--mantine-color-base-button)'} />
                </ActionIcon>
            );
        }
        return;
    };

    const handleSettingChange = (updatedSetting: UpdatedSetting) => {
        const previousSettingValue = categoryDefinition.settings.find(
            (value) => value.settingDefinition.identifier === updatedSetting.identifier
        )?.value;
        if (
            previousSettingValue === updatedSetting.value &&
            updatedSettings.find((value) => value.identifier === updatedSetting.identifier) !== undefined
        ) {
            setUpdatedSettings([...updatedSettings.filter((value) => value.identifier !== updatedSetting.identifier)]);
        } else if (previousSettingValue !== updatedSetting.value) {
            const newUpdatedSettings = [
                ...updatedSettings.filter((value) => value.identifier !== updatedSetting.identifier),
            ];
            newUpdatedSettings.push(updatedSetting);
            setUpdatedSettings(newUpdatedSettings);
        }
    };

    const getTriStateCategorySetting = () =>
        categoryDefinition.settings.find(
            (value) => value.settingDefinition.settingClass === SettingClass.TRISTATE_BOOLEAN
        );

    const getTriStateCategorySettingComponent = () => {
        const setting = getTriStateCategorySetting();
        if (setting) {
            return (
                <BasePropertySettingComponent
                    key={setting.settingDefinition.identifier + setting.settingDefinition.settingClass}
                    setting={setting}
                    onChange={onSettingChange}
                    propertyValidationInformation={{
                        ...propertyValidationInformation,
                        propertyIdentifier: setting.settingDefinition.identifier,
                    }}
                />
            );
        }
    };

    return (
        <Box>
            {getTriStateCategorySettingComponent()}
            <Card
                variant={'outlined'}
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    flexDirection: 'column',
                }}
            >
                <Card.Section style={{ padding: 10, paddingBottom: opened ? 10 : 0 }}>
                    <Box style={{ width: '100%', display: 'inline-flex', paddingBottom: '2px' }}>
                        <Box style={{ display: 'inline-flex', flexGrow: 1 }}>
                            <Text>{label}</Text>
                            <ActionIcon size={'sm'} variant={'transparent'} onClick={toggle}>
                                {opened ?
                                    <IconChevronDown color={'var(--mantine-color-base-button)'} />
                                :   <IconChevronRight color={'var(--mantine-color-base-button)'} />}
                            </ActionIcon>
                        </Box>
                        {getSaveButton()}
                    </Box>
                    <Collapse in={opened}>
                        <Box>
                            {categoryDefinition.settings.map(
                                (value) =>
                                    value.settingDefinition.settingClass !== SettingClass.TRISTATE_BOOLEAN && (
                                        <BasePropertySettingComponent
                                            key={
                                                value.settingDefinition.identifier +
                                                value.settingDefinition.settingClass
                                            }
                                            setting={value}
                                            onChange={handleSettingChange}
                                            disabled={
                                                getTriStateCategorySetting() === undefined ? false : (
                                                    getTriStateCategorySetting()?.value !==
                                                    TristateBoolean.INDETERMINATE
                                                )
                                            }
                                            propertyValidationInformation={{
                                                ...propertyValidationInformation,
                                                propertyIdentifier: value.settingDefinition.identifier,
                                            }}
                                        />
                                    )
                            )}
                        </Box>
                    </Collapse>
                </Card.Section>
            </Card>
        </Box>
    );
};
